import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";

import {
  LuFile,
  LuMessageCircleQuestion,
  LuListTodo,
  LuBookText,
  LuFolderOpen,
  LuBuilding,
  LuChartPie,
  LuExternalLink,
  LuLogOut,
  LuArrowRight,
  LuSearch,
  LuFolder,
  LuTag,
  LuZap,
  LuFlaskConical,
  LuMonitor,
  LuRefreshCw,
  LuHistory,
  LuSettings,
} from "react-icons/lu";

import API from "../api";
import { HiOutlineUserGroup } from "react-icons/hi";

const HEADER_HEIGHT = 32;
const ROW_HEIGHT = 44;

const CommandPalette = ({ isOpen, onClose, user, logout }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const commandListRef = useRef(null);
  const history = useHistory();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset search term when palette closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
    }
  }, [isOpen]);

  // Navigation helper (onClose is handled by executeCommand)
  const navigateTo = useCallback(
    (path) => {
      history.push(path);
    },
    [history],
  );

  const executeCommand = useCallback(
    (command, e) => {
      const newTab = !!(e && (e.metaKey || e.ctrlKey));
      if (command.path) {
        if (newTab) window.open(command.path, "_blank");
        else navigateTo(command.path);
      } else if (command.action) {
        command.action(command);
      }
      onClose();
    },
    [navigateTo, onClose],
  );

  // Search admissions (plain data only — icons/actions added in the commands memo)
  const admissionsQuery = useQuery({
    queryKey: ["command-palette", "admissions", debouncedSearchTerm],
    queryFn: async () => {
      const res = await API.get(`/admin/admission?search=${encodeURIComponent(debouncedSearchTerm)}`);
      return res.data.admissions.map((item) => {
        const hasName = item.patient?.firstName && item.patient?.lastName;
        const birthdate = item.patient?.birthdate ? new Date(item.patient.birthdate).toLocaleDateString("fr-FR") : null;
        return {
          id: item._id,
          name: hasName ? `${item.patient.firstName} ${item.patient.lastName}${birthdate ? ` (${birthdate})` : ""}` : item._id,
          description: `${item.number}${item.silReservationNumber ? ` · ${item.silReservationNumber}` : ""}${item.silAdmissionNumber ? ` · ${item.silAdmissionNumber}` : ""} - ${new Date(item.createdAt).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}`,
        };
      });
    },
    enabled: debouncedSearchTerm.length > 0,
  });

  // Search prescriptions (plain data only)
  const prescriptionsQuery = useQuery({
    queryKey: ["command-palette", "prescriptions", debouncedSearchTerm],
    queryFn: async () => {
      const res = await API.get(`/prescription/explorer?q=${encodeURIComponent(debouncedSearchTerm)}`);
      return res.data.map((item) => ({
        id: item._id,
        name: item.silAdmissionNumber,
        description: new Date(item.createdAt).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" }),
      }));
    },
    enabled: debouncedSearchTerm.length > 0,
  });

  // Static command definitions — only rebuilt when role/navigateTo/logout changes
  const allStaticCommands = useMemo(() => {
    const isAdmin = user.role === "admin" || user.role === "superadmin";

    return [
      {
        category: "Navigation",
        id: "admissions",
        name: "Admissions",
        description: "Accéder à la page d'admissions",
        icon: <LuFolderOpen className="h-4 w-4" />,
        action: () => navigateTo("/admissions"),
        keywords: ["admissions", "admission", "check", "validate"],
      },
      {
        category: "Navigation",
        id: "ordonnances",
        name: "Ordonnances",
        description: "Accéder aux ordonnances",
        icon: <LuFile className="h-4 w-4" />,
        path: "/prescription",
        keywords: ["ordonnances", "ordonnance", "browse"],
      },
      {
        category: "Navigation",
        id: "table",
        name: "Table",
        description: "Accéder à la table des codes",
        icon: <LuBookText className="h-4 w-4" />,
        path: "/table",
        keywords: ["table", "home", "dashboard"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "actions",
        name: "Actions",
        description: "Accéder aux actions",
        icon: <LuZap className="h-4 w-4" />,
        path: "/action",
        keywords: ["actions", "action", "effets", "automatisation"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "guidelines",
        name: "Recommandations",
        description: "Accéder aux recommandations",
        icon: <LuListTodo className="h-4 w-4" />,
        path: "/guideline",
        keywords: ["guidelines", "recommandations", "guide"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "questions",
        name: "Questions",
        description: "Accéder aux questions",
        icon: <LuMessageCircleQuestion className="h-4 w-4" />,
        path: "/question",
        keywords: ["questions", "faq"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "laboratory",
        name: "Laboratoires",
        description: "Accéder aux laboratoires",
        icon: <LuBuilding className="h-4 w-4" />,
        path: "/laboratory",
        keywords: ["laboratory", "laboratoires", "lab"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "clinic",
        name: "Cliniques",
        description: "Accéder aux cliniques et praticiens",
        icon: <HiOutlineUserGroup className="h-4 w-4" />,
        path: "/clinic",
        keywords: ["clinic", "cliniques", "clinique", "praticien", "clinician"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "codes",
        name: "Codes",
        description: "Accéder à la gestion des codes",
        icon: <LuTag className="h-4 w-4" />,
        path: "/code",
        keywords: ["codes", "code"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "machine",
        name: "Machines",
        description: "Accéder aux machines",
        icon: <LuMonitor className="h-4 w-4" />,
        path: "/machine",
        keywords: ["machine", "machines", "bot", "stream"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "sync",
        name: "Synchronisation",
        description: "Accéder au plan de synchronisation du SIL",
        icon: <LuRefreshCw className="h-4 w-4" />,
        path: "/sync",
        keywords: ["sync", "synchronisation", "agent", "kalisil"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "simulator",
        name: "Simulateur",
        description: "Accéder au simulateur",
        icon: <LuFlaskConical className="h-4 w-4" />,
        path: "/simulator",
        keywords: ["simulator", "simulateur", "simulation", "test"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "audit-log",
        name: "Journal d'audit",
        description: "Accéder au journal d'audit",
        icon: <LuHistory className="h-4 w-4" />,
        path: "/audit-log",
        keywords: ["audit", "journal", "log", "logs", "historique"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "settings",
        name: "Paramètres",
        description: "Accéder aux paramètres",
        icon: <LuSettings className="h-4 w-4" />,
        path: "/settings",
        keywords: ["settings", "paramètres", "parametres", "réglages", "configuration"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "stats",
        name: "Statistiques",
        description: "Accéder aux statistiques",
        icon: <LuChartPie className="h-4 w-4" />,
        path: "/",
        keywords: ["stats", "statistiques", "analytics"],
        role: "admin",
      },
      {
        category: "Navigation",
        id: "help",
        name: "Manuel d'utilisation",
        description: "Accéder au manuel d'utilisation",
        icon: <LuExternalLink className="h-4 w-4" />,
        action: () => window.open("https://help.querco.co/", "_blank"),
        keywords: ["help", "manuel", "documentation", "guide"],
      },
      {
        category: "Actions",
        id: "logout",
        name: "Se déconnecter",
        description: "Quitter l'application",
        icon: <LuLogOut className="h-4 w-4" />,
        action: logout,
        keywords: ["logout", "deconnexion", "exit", "quitter"],
      },
    ].filter((command) => command.role !== "admin" || isAdmin);
  }, [user.role, navigateTo, logout]);

  // Filtered and grouped commands
  const commands = useMemo(() => {
    const searchTermLower = searchTerm.toLowerCase();
    const filteredCommands = allStaticCommands.filter((command) => {
      if (command.keywords?.some((keyword) => keyword.toLowerCase().includes(searchTermLower))) return true;
      if (command.name?.toLowerCase().includes(searchTermLower)) return true;
      return false;
    });

    const groupedCommands = filteredCommands.reduce((acc, command) => {
      acc[command.category] = [...(acc[command.category] || []), command];
      return acc;
    }, {});

    if (debouncedSearchTerm.length > 0) {
      if (admissionsQuery.data?.length > 0) {
        groupedCommands["Admissions"] = admissionsQuery.data.map((item) => ({
          ...item,
          icon: <LuFolder className="h-4 w-4" />,
          action: (it) => navigateTo(`/admissions/${encodeURIComponent(it.id)}`),
        }));
      }
      if (prescriptionsQuery.data?.length > 0) {
        groupedCommands["Ordonnances"] = prescriptionsQuery.data.map((item) => ({
          ...item,
          icon: <LuFile className="h-4 w-4" />,
          path: `/prescription/${item.id}`,
        }));
      }
    }

    return groupedCommands;
  }, [allStaticCommands, searchTerm, debouncedSearchTerm, admissionsQuery.data, prescriptionsQuery.data]);

  // Flat render list: interleaved headers + commands for the virtualizer
  const flatRenderItems = useMemo(() => {
    const items = [];
    let commandIndex = 0;
    for (const [category, categoryCommands] of Object.entries(commands)) {
      if (categoryCommands.length === 0) continue;
      const isSearchCategory = category === "Admissions" || category === "Ordonnances";
      items.push({ type: "header", key: `header-${category}`, category, count: isSearchCategory ? categoryCommands.length : 0 });
      for (const command of categoryCommands) {
        items.push({ type: "command", key: command.id, commandIndex, ...command });
        commandIndex++;
      }
    }
    return items;
  }, [commands]);

  // Map from command-level activeIndex to flat virtualizer index (for scrollToIndex)
  const commandToFlatIndex = useMemo(() => {
    const map = [];
    for (let i = 0; i < flatRenderItems.length; i++) {
      if (flatRenderItems[i].type === "command") map.push(i);
    }
    return map;
  }, [flatRenderItems]);

  const commandCount = commandToFlatIndex.length;
  const activeCommandId = flatRenderItems[commandToFlatIndex[activeIndex]]?.id;

  const virtualizer = useVirtualizer({
    count: flatRenderItems.length,
    getScrollElement: () => commandListRef.current,
    estimateSize: (index) => (flatRenderItems[index].type === "header" ? HEADER_HEIGHT : ROW_HEIGHT),
    overscan: 50,
  });

  // Focus input and prevent background scrolling when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 10);

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Refs for keyboard handlers (avoid re-registering on every activeIndex change)
  const activeIndexRef = useRef(0);
  activeIndexRef.current = activeIndex;
  const commandCountRef = useRef(0);
  commandCountRef.current = commandCount;
  const commandToFlatIndexRef = useRef([]);
  commandToFlatIndexRef.current = commandToFlatIndex;
  const virtualizerRef = useRef(null);
  virtualizerRef.current = virtualizer;
  const flatRenderItemsRef = useRef([]);
  flatRenderItemsRef.current = flatRenderItems;

  // Handle keyboard events locally on the palette overlay to prevent them
  // from reaching document-level listeners in page components (code, clinic, etc.)
  const handlePaletteKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k")) {
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        onClose();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        const next = activeIndexRef.current < commandCountRef.current - 1 ? activeIndexRef.current + 1 : activeIndexRef.current;
        activeIndexRef.current = next;
        setActiveIndex(next);
        virtualizerRef.current?.scrollToIndex(commandToFlatIndexRef.current[next]);
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        const next = activeIndexRef.current > 0 ? activeIndexRef.current - 1 : 0;
        activeIndexRef.current = next;
        setActiveIndex(next);
        virtualizerRef.current?.scrollToIndex(commandToFlatIndexRef.current[next]);
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        const flatIndex = commandToFlatIndexRef.current[activeIndexRef.current];
        const item = flatRenderItemsRef.current[flatIndex];
        if (item) executeCommand(item, e);
        return;
      }
    },
    [onClose, executeCommand],
  );

  if (!isOpen) return null;

  const isSearching = debouncedSearchTerm.length > 0 && (admissionsQuery.isFetching || prescriptionsQuery.isFetching);
  const hasNoResults = debouncedSearchTerm.length > 0 && commandCount === 0 && !isSearching;
  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  return (
    <div className="fixed inset-0 z-[999] flex items-start justify-center bg-black/20 pt-[15vh]" onClick={onClose} onKeyDown={handlePaletteKeyDown}>
      <div
        className="w-full max-w-lg overflow-hidden rounded-lg bg-white"
        role="dialog"
        aria-modal="true"
        aria-labelledby="command-palette-title"
        onClick={(e) => e.stopPropagation()}
        style={{ isolation: "isolate" }}
      >
        {/* Search input */}
        <div className="flex items-center border-b border-gray-100 px-4 py-2.5">
          <LuSearch className="mr-3 h-4 w-4 flex-shrink-0 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
            placeholder="Rechercher une commande, admission, ordonnance..."
            value={searchTerm}
            onChange={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSearchTerm(e.target.value);
              setActiveIndex(0);
              if (commandListRef.current) commandListRef.current.scrollTop = 0;
            }}
          />
          <span className="ml-2 flex-shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-400">ESC</span>
        </div>

        {/* Virtualized command list */}
        <div ref={commandListRef} className="max-h-[60vh] overflow-y-auto">
          {flatRenderItems.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const item = flatRenderItems[virtualRow.index];
                const style = {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                };

                if (item.type === "header") {
                  return (
                    <div key={item.key} style={style} className="flex items-center gap-2 px-4 text-xs font-normal text-gray-500">
                      {item.category}
                      {item.count > 0 && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500">{item.count}</span>}
                    </div>
                  );
                }

                const isActive = activeCommandId === item.id;

                return (
                  <div
                    key={item.key}
                    data-command-id={item.id}
                    data-active={isActive || undefined}
                    style={style}
                    className="flex cursor-pointer items-center px-4 transition-colors hover:bg-gray-50 data-[active=true]:bg-primary-50/50"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      executeCommand(item, e);
                    }}
                    onMouseEnter={() => setActiveIndex(item.commandIndex)}
                  >
                    <div className={`mr-3 flex-shrink-0 ${isActive ? "text-primary-500" : "text-gray-400"}`}>{item.icon}</div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{item.name}</p>
                      {item.description && <p className="truncate text-xs text-gray-500">{item.description}</p>}
                    </div>
                    {isActive && <LuArrowRight className="ml-3 h-4 w-4 flex-shrink-0 text-gray-300" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* Loading state */}
          {isSearching && (
            <div className="flex items-center justify-center gap-2 py-6">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
              <span className="text-sm text-gray-400">Recherche en cours...</span>
            </div>
          )}

          {/* No results message */}
          {hasNoResults && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <LuSearch className="mb-2 h-5 w-5 text-gray-300" />
              <p className="text-sm">Aucun résultat pour &ldquo;{searchTerm}&rdquo;</p>
            </div>
          )}
        </div>

        {/* Footer with keyboard shortcuts */}
        <div className="flex justify-between border-t border-gray-100 px-4 py-2.5 text-xs text-gray-400">
          <div className="flex space-x-3">
            <div className="flex items-center gap-1">
              <span className="rounded bg-gray-100 px-1.5 py-0.5">↑</span>
              <span className="rounded bg-gray-100 px-1.5 py-0.5">↓</span>
              <span className="ml-0.5">Naviguer</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="rounded bg-gray-100 px-1.5 py-0.5">↵</span>
              <span className="ml-0.5">Sélectionner</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span className="rounded bg-gray-100 px-1.5 py-0.5">ESC</span>
            <span className="ml-0.5">Fermer</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
